/**
 * LoadingState — shared component for async loading states.
 * Renders a centered CircularProgress with an optional descriptive message.
 * Use `size="small"` for inline contexts (e.g., inside a form section).
 * Use `size="medium"` (default) for full-panel / full-page contexts.
 */

import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

interface LoadingStateProps {
  /** Accessible label / visible message shown below the spinner. */
  message?: string;
  /** Controls spinner size. Defaults to "medium". */
  size?: 'small' | 'medium';
  /** Minimum height of the container. Defaults to 160px for medium, 40px for small. */
  minHeight?: number | string;
}

const SPINNER_PX: Record<NonNullable<LoadingStateProps['size']>, number> = {
  small: 20,
  medium: 36,
};

export const LoadingState: React.FC<LoadingStateProps> = ({
  message,
  size = 'medium',
  minHeight,
}) => {
  const spinnerSize = SPINNER_PX[size];
  const defaultMinHeight = size === 'small' ? 40 : 160;

  return (
    <Box
      role="status"
      aria-label={message ?? 'Loading…'}
      sx={{
        display: 'flex',
        flexDirection: size === 'small' ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: size === 'small' ? 1.5 : 1,
        minHeight: minHeight ?? defaultMinHeight,
        py: size === 'small' ? 0 : 3,
      }}
    >
      <CircularProgress size={spinnerSize} aria-hidden="true" />
      {message && (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      )}
    </Box>
  );
};
